/**
 * Kallo theme behaviour.
 *
 * Everything here is progressive: the markup renders and reads correctly with
 * JavaScript disabled, and these components only add motion and interactivity
 * on top. Each custom element guards against being upgraded twice so it stays
 * safe inside the theme editor, which tears sections down and rebuilds them.
 */
(function () {
  'use strict';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
   * Scroll reveal
   *
   * Reveals [data-reveal] elements as they enter the viewport. Siblings that
   * share a [data-reveal-group] parent are numbered so their transition-delay
   * staggers, which produces the "each line of text appears with the next
   * scroll" behaviour the story section asks for.
   * ------------------------------------------------------------------- */
  function initReveal() {
    const targets = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!targets.length) return;

    // Without IntersectionObserver, or with motion suppressed, show everything.
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    document.querySelectorAll('[data-reveal-group]').forEach((group) => {
      group.querySelectorAll(':scope > [data-reveal]').forEach((child, i) => {
        child.style.setProperty('--reveal-index', String(i));
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    targets.forEach((el) => observer.observe(el));
  }

  /* ---------------------------------------------------------------------
   * Pinned-hero scroll progress
   *
   * A pinned hero stays put while the next section rides up over it. This
   * publishes how far that has got as `--hero-scroll` (0 -> 1) on the hero, and
   * the section's own CSS decides what to do with it — here, blur and darken
   * the photograph and retire the copy.
   *
   * Progress is measured from the section that does the covering rather than
   * from the window, so a hero that is not the first section on the page still
   * reads correctly. Values are rounded to twentieths: a blur re-rasterises the
   * layer it is on, so this keeps that to a couple of dozen steps across the
   * whole travel instead of one per frame.
   * ------------------------------------------------------------------- */
  const PINNED_STEPS = 20;
  let pinnedScrollBound = false;

  function initPinnedScroll() {
    const heroes = Array.from(document.querySelectorAll('[data-pinned]'));

    // With motion suppressed the hero is not pinned at all (see critical.css),
    // so it must stay sharp and its copy legible.
    if (!heroes.length || prefersReducedMotion()) {
      heroes.forEach((hero) => hero.style.setProperty('--hero-scroll', '0'));
      return;
    }

    let queued = false;

    const measure = () => {
      queued = false;

      heroes.forEach((hero) => {
        const section = hero.closest('.shopify-section') || hero;
        const next = section.nextElementSibling;
        const height = section.offsetHeight;
        if (!next || !height) return;

        // `next` starts one hero-height down the page and ends flush with the
        // top of the viewport once it has covered the hero completely.
        const covered = 1 - next.getBoundingClientRect().top / height;
        const clamped = covered < 0 ? 0 : covered > 1 ? 1 : covered;
        const stepped = Math.round(clamped * PINNED_STEPS) / PINNED_STEPS;

        if (stepped !== hero.kalloScrollProgress) {
          hero.kalloScrollProgress = stepped;
          hero.style.setProperty('--hero-scroll', String(stepped));
        }
      });
    };

    const schedule = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(measure);
    };

    if (!pinnedScrollBound) {
      pinnedScrollBound = true;
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
    }

    measure();
  }

  /* ---------------------------------------------------------------------
   * <kallo-rotator> — cycles its children, one at a time.
   *
   * Used by the announcement bar: "text changes, rotates so several texts can
   * play". All messages are in the DOM for crawlers and screen readers; only
   * the active one is visible. Pauses while the tab is hidden or the pointer
   * rests on it, so a reader is never cut off mid-sentence.
   * ------------------------------------------------------------------- */
  class KalloRotator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised) return;
      this.dataset.initialised = 'true';

      this.items = Array.from(this.querySelectorAll('[data-rotator-item]'));
      if (this.items.length < 2) return;

      this.index = 0;
      this.interval = Number(this.dataset.interval || 5000);
      this.paused = false;

      this.addEventListener('mouseenter', () => (this.paused = true));
      this.addEventListener('mouseleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));

      this.timer = window.setInterval(() => {
        if (this.paused || document.hidden) return;
        this.show(this.index + 1);
      }, this.interval);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    show(next) {
      const target = ((next % this.items.length) + this.items.length) % this.items.length;
      this.items[this.index].removeAttribute('data-active');
      this.items[target].setAttribute('data-active', '');
      this.index = target;
    }
  }

  /* ---------------------------------------------------------------------
   * <kallo-drawer> — the slide-in menu behind the hamburger.
   *
   * Wraps a native <dialog>, so the browser supplies the focus trap, the
   * Escape handler and the inert backdrop rather than us reimplementing them.
   * ------------------------------------------------------------------- */
  class KalloDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised) return;
      this.dataset.initialised = 'true';

      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      const openers = document.querySelectorAll(`[data-drawer-open="${this.id}"]`);
      openers.forEach((btn) =>
        btn.addEventListener('click', () => this.dialog.showModal())
      );

      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.dialog.close())
      );

      // Clicking the backdrop — i.e. the dialog element itself, outside its
      // content box — closes the drawer.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }
  }

  /* ---------------------------------------------------------------------
   * <kallo-fan> — the wheel of photos, turning under its own steam.
   *
   * Endless by construction. A card's place on the arc is how far it is from
   * the wheel's current angle, measured *around the whole set*: go past the
   * last photo and you are back at the first. So the wheel has no start and no
   * end to conceal, and no cloned cards to keep in step — ten photos are ten
   * positions on one circle.
   *
   * Photos do not fade. They ride up one side of the arc and sink down the
   * other, and the section's own edge crops them on the way — so a photo
   * leaves by going somewhere, not by dissolving. That crop is also what hides
   * the jump from one end of the run to the other: by the time a photo reaches
   * it, the arc has carried it below the window.
   *
   * The loop is paused whenever it cannot be seen — off screen, another tab,
   * or the pointer resting on it — so an animation nobody is watching costs
   * nothing.
   * ------------------------------------------------------------------- */
  class KalloFan extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised) return;
      this.dataset.initialised = 'true';

      this.items = Array.from(this.querySelectorAll('.ugc__item'));
      this.count = Number(this.dataset.count || this.items.length);
      this.step = Number(this.dataset.step || 18);
      if (this.count < 2) return;

      const seconds = Number(this.dataset.secondsPerPhoto || 3);
      // Positions per millisecond: one "position" is one photo's place on the
      // wheel, so the setting reads as seconds per photo rather than as a
      // rotation speed nobody can picture.
      this.rate = 1 / Math.max(seconds, 0.1) / 1000;
      if (this.dataset.direction === 'reverse') this.rate = -this.rate;

      // Start with the run centred on the top of the arc, matching the resting
      // fan Liquid rendered.
      this.turn = (this.count - 1) / 2;
      this.layout();

      // A still fan is the right resting state for anyone who has asked for
      // less motion, so the loop simply never starts.
      if (!this.hasAttribute('data-spin') || prefersReducedMotion()) return;

      this.onVisibility = () => this.sync();
      document.addEventListener('visibilitychange', this.onVisibility);

      if (this.hasAttribute('data-pause-on-hover')) {
        this.addEventListener('pointerenter', () => { this.hovered = true; this.sync(); });
        this.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); });
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => { this.onScreen = entry.isIntersecting; this.sync(); },
          { rootMargin: '10% 0px' }
        );
        this.observer.observe(this);
      } else {
        this.onScreen = true;
        this.sync();
      }
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.observer?.disconnect();
    }

    /** Runs only while the wheel is on screen, in a visible tab, un-hovered. */
    sync() {
      const shouldRun = this.onScreen && !document.hidden && !this.hovered;
      if (shouldRun) this.start(); else this.stop();
    }

    start() {
      if (this.frame) return;
      // Dropping the stale timestamp stops the wheel lurching forward by
      // however long it was paused.
      this.last = null;
      this.frame = window.requestAnimationFrame((t) => this.tick(t));
    }

    stop() {
      if (!this.frame) return;
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    tick(now) {
      if (this.last !== null) {
        const elapsed = Math.min(now - this.last, 100);
        this.turn = (this.turn + elapsed * this.rate) % this.count;
        this.layout();
      }
      this.last = now;
      this.frame = window.requestAnimationFrame((t) => this.tick(t));
    }

    /**
     * Places every card for the wheel's current angle.
     *
     * The distance from the wheel's position is taken the short way round, so
     * it always lands in the half-open range either side of the top of the
     * arc — which is exactly what makes the run endless.
     */
    layout() {
      const half = this.count / 2;

      this.items.forEach((item, i) => {
        let offset = (((i - this.turn) % this.count) + this.count) % this.count;
        if (offset > half) offset -= this.count;

        const angle = offset * this.step;
        item.style.setProperty('--a', angle.toFixed(2));

        // Stacked the way photographs land when you lay them out on a table:
        // each one covers a corner of the one before it, always the same way
        // round. Ordering by distance from the centre instead made the middle
        // photo jump to the front and the overlap reverse halfway along, which
        // reads as a carousel rather than a pile.
        //
        // The card that arrived most recently sits on top, so the run of
        // overlaps follows the direction of travel and flips with it.
        const arrivalOrder = this.rate >= 0 ? offset + half : half - offset;
        item.style.zIndex = String(Math.max(0, Math.round(arrivalOrder * 10)));
      });
    }

  }

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  define('kallo-rotator', KalloRotator);
  define('kallo-drawer', KalloDrawer);
  define('kallo-fan', KalloFan);

  document.documentElement.classList.remove('no-js');

  function init() {
    initReveal();
    initPinnedScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor swaps section markup in without a page load; re-scan so
  // freshly inserted elements still reveal instead of staying invisible, and so
  // a hero rebuilt with new settings picks its scroll progress back up.
  document.addEventListener('shopify:section:load', init);
})();
